import type { UserStateItemKey } from "../../types/user-state";
import {
  isApplicationKitKey,
  isAppliedJobKey,
  isBrowsedJobKey,
  isFeedbackKey,
  isSavedCompanyKey,
  isSavedJobKey,
  isSavedRoleKey,
  isSavedSearchFolderKey,
  isSavedSearchKey,
  isViewedJobKey,
} from "../../types/user-state";

/**
 * Path segment for {@link userStateApi} requests. Job-related keys use `/job/{bucket}/{jobId}`,
 * saved-company keys use `/company/{companyId}`, saved-search keys use `/search/{searchId}`,
 * saved-search-folder keys use `/searchFolder/{folderId}`, feedback keys use
 * `/feedback/{feedbackId}`, and application-kit keys use `/application-kit/{jobId}` so the URL
 * never contains a raw `#` (which gets mangled by some proxies/CDNs even when percent-encoded,
 * before the request reaches the server).
 */
export function buildUserStateItemApiPath(itemKey: UserStateItemKey): string {
  if (isSavedJobKey(itemKey)) {
    return `/job/saved/${encodeURIComponent(itemKey.slice("savedJob#".length))}`;
  }
  if (isViewedJobKey(itemKey)) {
    return `/job/viewed/${encodeURIComponent(itemKey.slice("viewedJob#".length))}`;
  }
  if (isAppliedJobKey(itemKey)) {
    return `/job/applied/${encodeURIComponent(itemKey.slice("appliedJob#".length))}`;
  }
  if (isBrowsedJobKey(itemKey)) {
    return `/job/browsed/${encodeURIComponent(itemKey.slice("browsedJob#".length))}`;
  }
  if (isSavedCompanyKey(itemKey)) {
    return `/company/${encodeURIComponent(itemKey.slice("savedCompany#".length))}`;
  }
  // Must be checked before isSavedSearchKey: "savedSearchFolder#" does not start with
  // "savedSearch#" (no literal "#" until after "Folder"), so the two never collide, but
  // isSavedSearchFolderKey is the more specific check and belongs first for clarity.
  if (isSavedSearchFolderKey(itemKey)) {
    return `/searchFolder/${encodeURIComponent(itemKey.slice("savedSearchFolder#".length))}`;
  }
  if (isSavedSearchKey(itemKey)) {
    return `/search/${encodeURIComponent(itemKey.slice("savedSearch#".length))}`;
  }
  if (isSavedRoleKey(itemKey)) {
    return `/role/${encodeURIComponent(itemKey.slice("savedRole#".length))}`;
  }
  if (isFeedbackKey(itemKey)) {
    return `/feedback/${encodeURIComponent(itemKey.slice("feedback#".length))}`;
  }
  if (isApplicationKitKey(itemKey)) {
    return `/application-kit/${encodeURIComponent(itemKey.slice("applicationKit#".length))}`;
  }
  return `/${encodeURIComponent(itemKey)}`;
}
